/**
 * Shared "Clear all" reset chip for grid filter toolbars.
 *
 * Used by both FilterBar (the shared grid filter component) and grids
 * that compose their own toolbar (e.g. the radio search grid), so the
 * reset affordance looks identical everywhere. Keeps the toolbar filter
 * chips' dimensions and border, but sits on a slightly offset surface
 * with a leading ✕ so it reads as a distinct reset action rather than
 * another filter chip.
 *
 * Contains:
 * - ClearAllChip — the reset chip (default export)
 */

import Clear from '@mui/icons-material/Clear';
import Chip from '@mui/material/Chip';
import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { toolbarChipSx } from './toolbarStyles';

export default function ClearAllChip({ onClick, sx, ...rest }) {
  const { t } = useTranslation();
  return (
    <Chip
      icon={<Clear sx={{ fontSize: 16 }} />}
      size="small"
      variant="outlined"
      label={t('general.clearAll', 'Clear all')}
      onClick={onClick}
      sx={[
        toolbarChipSx,
        {
          '&.MuiChip-outlined, &.MuiChip-filled': { backgroundColor: 'var(--surface-secondary)' },
          '&.MuiChip-outlined:hover, &.MuiChip-filled:hover': { backgroundColor: 'var(--md-surface-container)' },
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
      {...rest}
    />
  );
}

ClearAllChip.propTypes = {
  /** Reset handler — clears all active filters. */
  onClick: PropTypes.func.isRequired,
  /** Optional sx overrides merged after the base style. */
  sx: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
};
